import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from 'org-graph/locales/en.json';
import sv from 'org-graph/locales/sv.json';
import { uiCopy } from './uiCopy';

export const LANGUAGE_STORAGE_KEY = 'breadify.uiLanguage';
export const supportedLanguages = ['en', 'sv'] as const;
export type AppLanguage = typeof supportedLanguages[number];

export function isAppLanguage(value: unknown): value is AppLanguage {
  return typeof value === 'string' && supportedLanguages.includes(value as AppLanguage);
}

export function getInitialLanguage(): AppLanguage {
  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isAppLanguage(storedLanguage) ? storedLanguage : 'sv';
  } catch {
    return 'sv';
  }
}

export function getAppLanguage(language = i18n.resolvedLanguage): AppLanguage {
  return language?.toLowerCase().startsWith('sv') ? 'sv' : 'en';
}

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: { ...en, ui: uiCopy.en } },
      sv: { translation: { ...sv, ui: uiCopy.sv } },
    },
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: [...supportedLanguages],
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    returnNull: false,
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (_languages, _namespace, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] Missing translation: ${key}`);
      }
    },
  });

function applyLanguage(language: string) {
  const appLanguage = getAppLanguage(language);
  document.documentElement.lang = appLanguage;
  document.documentElement.dir = 'ltr';
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, appLanguage);
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
}

applyLanguage(i18n.resolvedLanguage ?? 'sv');
i18n.on('languageChanged', applyLanguage);

export async function changeAppLanguage(language: AppLanguage) {
  await i18n.changeLanguage(language);
}

export default i18n;
